import Form from 'antd/es/form/index'
import { FormInstance, message } from 'antd/lib'
import Row from 'antd/es/grid/row'
import IdeyaRangePicker from '../../../../common/antd/ideya-rangepicker.tsx'
import { Checkbox } from 'antd/lib/index'
import IdeyaTypography from '../../../../common/antd/ideya-typography.tsx'
import { useCallback, useEffect, useState } from 'react'
import Col from 'antd/es/grid/col'
import { IdeyaButton } from '../../../../common/antd/ideya-button/ideya-button.tsx'
import { useIsMobile } from '../../../../common/hooks/use-is-mobile.ts'
import dayjs from 'dayjs'
import { useCreateBlockLeaveMutation } from '../../../../apis/block-leave/block-leave-api.api.ts'
import notification from 'antd/es/notification/index'
import { getErrorDescription } from '../../../../redux/utils.ts'
import { useTranslation } from 'react-i18next'
import './user-block-leave-form.scss'

export type UserBlockLeaveFormFieldTypes = {
  userId: string
  dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null
  startDate: string
  endDate: string
  closeIdeas: boolean
}

interface UserBlockLeaveFormProps {
  userId: string | null
  form: FormInstance<UserBlockLeaveFormFieldTypes>
  handleCloseModal: () => void
}

export const UserBlockLeaveForm: React.FC<UserBlockLeaveFormProps> = ({
  userId,
  form,
  handleCloseModal,
}) => {
  const { t } = useTranslation()
  const [isLiquidateChecked, setIsLiquidateChecked] = useState(false)
  const isMobile = useIsMobile(768)
  const [messageApi, messageContextHolder] = message.useMessage()
  const [notificationApi, notificationContextHolder] =
    notification.useNotification()

  const [createBlockLeave, { isLoading, isSuccess, isError, error, reset }] =
    useCreateBlockLeaveMutation()

  const onFinish = (values: UserBlockLeaveFormFieldTypes) => {
    const transformedValues = {
      userId: userId ?? '',
      startDate: values.dateRange?.[0].format('YYYY-MM-DD') || '',
      endDate: values.dateRange?.[1].format('YYYY-MM-DD') || '',
      closeIdeas: values.closeIdeas,
    }
    createBlockLeave({
      ...transformedValues,
    })
  }

  const success = useCallback(() => {
    messageApi.open({
      type: 'success',
      content: t(
        'user_management.user_modal.block_leave_modal.form.success_message'
      ),
    })
  }, [messageApi, t])

  const errorNotification = useCallback(
    (description: string) => {
      notificationApi.error({
        message: 'Error',
        description,
        placement: 'topRight',
      })
    },
    [notificationApi]
  )

  useEffect(() => {
    if (isSuccess) {
      success()
      handleCloseModal()
      reset()
    }
  }, [isSuccess, form, handleCloseModal, reset, success])

  useEffect(() => {
    if (isError) {
      const description = getErrorDescription(error)
      errorNotification(description)
      reset()
    }
  }, [isError, error, reset, errorNotification])

  return (
    <>
      {messageContextHolder}
      {notificationContextHolder}
      <Form form={form} layout={'vertical'} onFinish={onFinish}>
        <Form.Item
          label={t(
            'user_management.user_modal.block_leave_modal.form.select_leave_dates'
          )}
          name={'dateRange'}
          rules={[
            {
              required: true,
              message: t(
                'user_management.user_modal.block_leave_modal.form.please_select_leave_dates'
              ),
            },
          ]}
          getValueFromEvent={(dates) => dates}
        >
          <IdeyaRangePicker
            presets={[]}
            disableHistoricalDates={true}
            maxDate={dayjs().add(1, 'year').endOf('month')}
            className={'user-block-leave-form__date-range-picker'}
            style={{ width: '100%' }}
            placeholder={[
              t('user_management.user_modal.block_leave_modal.form.start_date'),
              t('user_management.user_modal.block_leave_modal.form.end_date'),
            ]}
          />
        </Form.Item>
        <Form.Item
          label={
            <span className={'user-block-leave-form__liquidate-label'}>
              <IdeyaTypography.Text>
                {t(
                  'user_management.user_modal.block_leave_modal.form.liquidate_setup'
                )}
              </IdeyaTypography.Text>
              <IdeyaTypography.Text type={'secondary'}>
                (
                {t(
                  'user_management.user_modal.block_leave_modal.form.optional'
                )}
                )
              </IdeyaTypography.Text>
            </span>
          }
          valuePropName={'checked'}
          name={'closeIdeas'}
        >
          <div
            className={`user-block-leave-form__liquidate-wrapper${isLiquidateChecked ? ' user-block-leave-form__liquidate-wrapper--active' : ''}`}
          >
            <Row>
              <Checkbox
                onChange={(e) => setIsLiquidateChecked(e.target.checked)}
              >
                <IdeyaTypography.Text>
                  {t(
                    'user_management.user_modal.block_leave_modal.form.liquidate_all_my_ideas'
                  )}
                </IdeyaTypography.Text>
              </Checkbox>
            </Row>
            <Row>
              <IdeyaTypography.Text type={'secondary'}>
                {t(
                  'user_management.user_modal.block_leave_modal.form.liquidate_all_my_ideas_hint'
                )}{' '}
              </IdeyaTypography.Text>
            </Row>
          </div>
        </Form.Item>
        <Row
          justify={'end'}
          className={`user-block-leave-form__actions${isMobile ? ' user-block-leave-form__actions--mobile' : ''}`}
        >
          <Col>
            <Row gutter={[8, 8]}>
              <>
                <Col>
                  <IdeyaButton ghost onClick={handleCloseModal}>
                    {t('common.cancel')}
                  </IdeyaButton>
                </Col>
                <Col>
                  <IdeyaButton loading={isLoading} htmlType={'submit'}>
                    {t('common.ok')}
                  </IdeyaButton>
                </Col>
              </>
            </Row>
          </Col>
        </Row>
      </Form>
    </>
  )
}
